// src/web/client/src/utils/shopContext.js
//
// THE single shop-context resolver (priority-3 fix).
//
// The bug this kills, proven live in the server logs: every surface used to
// read shop_domain localStorage-FIRST, so a value from a previous session
// silently beat the shop actually open in the admin — two dev stores showed
// the same data, the sidebar showed a third store's name, and the server
// received `shop=ecomoria-test` while the merchant was inside billing-dev2.
//
// New order of truth:
//   1. `?shop=` in the URL — the admin passes it on every embedded entry;
//      when present it is AUTHORITATIVE. A validated value is persisted,
//      and if it DIFFERS from the previous session's shop, every
//      shop-scoped key is purged (name, ids, host, tokens) so nothing
//      from shop A can leak into shop B's session.
//   2. Stored value (localStorage/sessionStorage) — SPA navigation and
//      revisits, where the URL no longer carries the param.
//   3. null — unknown store; callers show their honest error state
//      (never a demo fallback — that was #89).
//
// Deliberate demo mode (isDemo/DemoModeButton) is unaffected: it stores
// demo-shop explicitly and no ?shop= param is present in that flow.

const SHOP_DOMAIN_RE = /^[a-z0-9][a-z0-9-]*\.myshopify\.com$/i;

// Everything that belongs to ONE shop's session. Purged on shop switch so
// stale identity can never leak across stores. The fresh id_token in the
// switch URL is re-saved right after the purge (it belongs to the NEW shop).
const SHOP_SCOPED_KEYS = [
  'shop_id',
  'shopId',
  'shopName',
  'shopify_host',
  'token', // legacy app JWT — issued per shop at OAuth
  'shopify_session_token',
];

// Marker owned EXCLUSIVELY by this resolver. We can't compare against
// shop_domain itself — other writers (App.jsx, ShopifyAppBridge) may have
// already overwritten it with the new shop before we run, which would hide
// the switch and leave stale scoped keys alive.
const LAST_SHOP_KEY = 'last_shop_domain';

function readStored() {
  const v =
    localStorage.getItem('shop_domain') ||
    localStorage.getItem('shopDomain') ||
    sessionStorage.getItem('shop_domain') ||
    sessionStorage.getItem('shopDomain');
  return v && v !== 'undefined' && v !== 'null' && v !== '' ? v : null;
}

function persistDomain(domain) {
  localStorage.setItem('shop_domain', domain);
  localStorage.setItem('shopDomain', domain);
  sessionStorage.setItem('shop_domain', domain);
  sessionStorage.setItem('shopDomain', domain);
}

function purgeShopScopedKeys() {
  SHOP_SCOPED_KEYS.forEach((k) => {
    localStorage.removeItem(k);
    sessionStorage.removeItem(k);
  });
}

/**
 * Resolve the current shop domain. URL wins; storage is the fallback;
 * null means "honestly unknown".
 */
export function getShopDomain() {
  const params = new URLSearchParams(window.location.search);
  const urlShopRaw = (params.get('shop') || '').trim().toLowerCase();

  if (urlShopRaw && SHOP_DOMAIN_RE.test(urlShopRaw)) {
    const lastShop = localStorage.getItem(LAST_SHOP_KEY);
    if (lastShop && lastShop !== urlShopRaw) {
      // SHOP SWITCH — kill everything belonging to the previous shop.
      purgeShopScopedKeys();
      // The switch URL carries a fresh id_token for the NEW shop — restore
      // it so the very first API calls are authenticated.
      const freshIdToken = params.get('id_token');
      if (freshIdToken) {
        localStorage.setItem('shopify_session_token', freshIdToken);
      }
      console.log(`[ShopContext] Shop switch: ${lastShop} → ${urlShopRaw} (stale session purged)`);
    }
    localStorage.setItem(LAST_SHOP_KEY, urlShopRaw);
    persistDomain(urlShopRaw);
    return urlShopRaw;
  }

  const stored = readStored();
  if (stored) {
    // Keep the switch marker coherent for sessions that started without a
    // URL param (e.g. deliberate demo mode).
    if (!localStorage.getItem(LAST_SHOP_KEY)) {
      localStorage.setItem(LAST_SHOP_KEY, stored.toLowerCase());
    }
    return stored;
  }

  return null;
}

// (getShopDisplayName — the localStorage.shopName read — is gone: the store's name
// has one source, Shop.shop_name via hooks/useShopProfile. 'shopName' stays in
// SHOP_SCOPED_KEYS so a stale value from an old session is still purged.)

// Exported for tests.
export const _internal = { SHOP_DOMAIN_RE, SHOP_SCOPED_KEYS, LAST_SHOP_KEY };
